import { ButtonContent } from '../ui/ButtonContent';
import { useEffect, useId, useRef, useState } from 'react';
import type { InterviewSession, ResponseStatus } from '../../types';
import { Modal } from '../ui/Modal';
import { responseStatusConfig } from '../../lib/admin-utils';
import { getExportDateBounds, type CampaignExportFilters } from '../../lib/campaign-csv';
import { downloadCampaignCsv } from '../../services/campaignExportService';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { validateExportFilters } from '../../lib/campaign-export-filters';
import { CampaignAnswerFilters } from './CampaignAnswerFilters';

/** Campaign and dismissal callback for the CSV export dialog. */
export interface CampaignExportModalProps {
  session: InterviewSession;
  onClose: () => void;
  initialFilters?: CampaignExportFilters;
  conversationIds?: string[];
}

/** Filters and download controls for a campaign's answers. */
export function CampaignExportModal({ session, onClose, initialFilters, conversationIds }: CampaignExportModalProps) {
  const id = useId();
  const [filters, setFilters] = useState<CampaignExportFilters>(initialFilters ?? { startDate: '', endDate: '', status: undefined });
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [empty, setEmpty] = useState(false);
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => request.current?.abort(), []);

  let dateError = '';
  try { getExportDateBounds(filters); }
  catch { dateError = filters.startDate && filters.endDate && filters.startDate > filters.endDate
    ? 'End date must be on or after start date.' : 'Choose a valid date.'; }

  let answersValid = true;
  try { validateExportFilters(filters, session.form_schema ?? []); } catch { answersValid = false; }

  /** A changed filter starts a new attempt without discarding the other selections. */
  function updateFilters(change: Partial<CampaignExportFilters>) {
    setFilters(current => ({ ...current, ...change }));
    setFailed(false);
    setEmpty(false);
  }

  /** Stop paging before dismissing so a cancelled dialog cannot trigger a later download. */
  function close() {
    request.current?.abort();
    onClose();
  }

  /** Keep failure and empty results in the dialog, preserving the chosen filters. */
  async function download() {
    if (request.current || dateError || !answersValid) return;
    const controller = new AbortController();
    request.current = controller;
    setBusy(true);
    setEmpty(false);
    try {
      const count = conversationIds
        ? await downloadCampaignCsv(session, filters, controller.signal, conversationIds)
        : await downloadCampaignCsv(session, filters, controller.signal);
      if (controller.signal.aborted) return;
      setFailed(false);
      if (count) onClose();
      else setEmpty(true);
    } catch (error) {
      if (controller.signal.aborted) return;
      captureException(error instanceof Error ? error : new Error('Campaign CSV export failed'), 'CampaignExportModal.download', { sessionId: session.id });
      setFailed(true);
    } finally {
      if (!controller.signal.aborted) setBusy(false);
      if (request.current === controller) request.current = null;
    }
  }

  const inputClass = 'w-full min-w-0 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground [color-scheme:light] dark:[color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50';
  return (
    <Modal open onClose={close} title="Export CSV" maxWidth="max-w-xl">
      <form onSubmit={event => { event.preventDefault(); void download(); }}>
        <fieldset disabled={busy} className="max-h-[60vh] space-y-4 overflow-y-auto px-1">
          <div>
            <label htmlFor={`${id}-status`} className="mb-2 block text-sm text-muted-foreground">Status</label>
            <select id={`${id}-status`} value={filters.status ?? ''} className={inputClass}
              onChange={event => updateFilters({ status: event.target.value as ResponseStatus || undefined })}>
              <option value="">All statuses</option>
              {Object.entries(responseStatusConfig).map(([status, config]) => (
                <option key={status} value={status}>{config.label}</option>
              ))}
            </select>
          </div>
          <fieldset>
            <legend className="mb-2 text-sm text-muted-foreground">Created date</legend>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor={`${id}-from`} className="mb-1 block text-sm text-muted-foreground">From date</label>
                <input id={`${id}-from`} type="date" value={filters.startDate} className={inputClass}
                  onChange={event => updateFilters({ startDate: event.target.value })} />
              </div>
              <div>
                <label htmlFor={`${id}-to`} className="mb-1 block text-sm text-muted-foreground">To date</label>
                <input id={`${id}-to`} type="date" value={filters.endDate} min={filters.startDate || undefined} className={inputClass}
                  aria-invalid={Boolean(dateError)} aria-describedby={dateError ? `${id}-dates-error` : undefined}
                  onChange={event => updateFilters({ endDate: event.target.value })} />
              </div>
            </div>
            <div id={`${id}-dates-error`} role="alert" className="text-sm text-danger-soft">{dateError && <p className="mt-2">{dateError}</p>}</div>
          </fieldset>
          <CampaignAnswerFilters fields={session.form_schema ?? []} filters={filters} onChange={updateFilters} inputClass={inputClass} />
        </fieldset>
        <div role="alert" className="text-sm text-danger-soft">{failed && !busy && <p className="mt-4">Could not export CSV.</p>}</div>
        <div role="status" className="text-sm text-muted-foreground">{empty && <p className="mt-4">No responses match these filters.</p>}</div>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={close} className="rounded-lg px-4 py-2 text-sm text-muted-foreground hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-ring">Cancel</button>
          <button type="submit" disabled={busy || Boolean(dateError) || !answersValid} aria-busy={busy}
            className="flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:bg-accent-hover disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-ring">
            <ButtonContent loading={busy}>{failed ? 'Try again' : 'Download CSV'}</ButtonContent>
          </button>
        </div>
      </form>
    </Modal>
  );
}
